/**
 * Footer Component
 *
 * Adaptive footer that changes structure based on LayoutTheme.
 * - modern-cards: Clean footer with links
 * - matrix-terminal: ASCII art footer with system info
 * - mac-os-classic: Classic status bar
 * - executive-dashboard: Minimal footer
 */

import { useLayoutTheme } from '@/contexts/LayoutThemeContext'
import { Container } from '@/components/ui/Container'

export function Footer() {
  const { themeConfig } = useLayoutTheme()
  const currentYear = new Date().getFullYear()

  // Modern Cards Layout
  if (themeConfig.id === 'modern-cards') {
    return (
      <footer
        style={{
          backgroundColor: 'var(--color-surface)',
          borderTop: '1px solid rgba(0, 0, 0, 0.1)',
          padding: 'var(--spacing-2xl) 0',
          marginTop: 'auto',
        }}
      >
        <Container size="xl">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 'var(--spacing-2xl)',
            }}
          >
            <div>
              <h3
                style={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  marginBottom: 'var(--spacing-md)',
                  color: 'var(--color-text-primary)',
                }}
              >
                About
              </h3>
              <p
                style={{
                  fontSize: '0.875rem',
                  color: 'var(--color-text-secondary)',
                  lineHeight: 1.6,
                }}
              >
                Full-stack engineer specializing in secure systems and modern web technologies.
              </p>
            </div>
            <div>
              <h3
                style={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  marginBottom: 'var(--spacing-md)',
                  color: 'var(--color-text-primary)',
                }}
              >
                Connect
              </h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ marginBottom: 'var(--spacing-sm)' }}>
                  <a
                    href="https://github.com/auge2u"
                    style={{ color: 'var(--color-accent)', textDecoration: 'none' }}
                  >
                    GitHub @auge2u
                  </a>
                </li>
                <li style={{ marginBottom: 'var(--spacing-sm)' }}>
                  <a
                    href="https://github.com/a2m2c"
                    style={{ color: 'var(--color-accent)', textDecoration: 'none' }}
                  >
                    GitHub @a2m2c
                  </a>
                </li>
                <li style={{ marginBottom: 'var(--spacing-sm)' }}>
                  <a
                    href="https://linkedin.com/in/agustinmusi"
                    style={{ color: 'var(--color-accent)', textDecoration: 'none' }}
                  >
                    LinkedIn
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h3
                style={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  marginBottom: 'var(--spacing-md)',
                  color: 'var(--color-text-primary)',
                }}
              >
                Legal
              </h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ marginBottom: 'var(--spacing-sm)' }}>
                  <a
                    href="/privacy"
                    style={{ color: 'var(--color-accent)', textDecoration: 'none' }}
                  >
                    Privacy Policy
                  </a>
                </li>
                <li style={{ marginBottom: 'var(--spacing-sm)' }}>
                  <a
                    href="/terms"
                    style={{ color: 'var(--color-accent)', textDecoration: 'none' }}
                  >
                    Terms of Service
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div
            style={{
              marginTop: 'var(--spacing-2xl)',
              paddingTop: 'var(--spacing-lg)',
              borderTop: '1px solid rgba(0, 0, 0, 0.1)',
              textAlign: 'center',
              fontSize: '0.875rem',
              color: 'var(--color-text-secondary)',
            }}
          >
            © {currentYear} Agustin Musi. All rights reserved.
          </div>
        </Container>
      </footer>
    )
  }

  // Matrix Terminal Layout
  if (themeConfig.id === 'matrix-terminal') {
    return (
      <footer
        style={{
          backgroundColor: '#000',
          color: '#0f0',
          padding: 'var(--spacing-lg) 0',
          fontFamily: 'monospace',
          borderTop: '1px solid #0f0',
          marginTop: 'auto',
        }}
      >
        <Container size="xl">
          <pre style={{ margin: 0, fontSize: '0.875rem' }}>
            {`
╔════════════════════════════════════════════════════════╗
║  SYSTEM_INFO:                                         ║
║  > GitHub: @auge2u | @a2m2c                           ║
║  > LinkedIn: /in/agustinmusi                          ║
║  > Year: ${currentYear}                                         ║
║  > Status: ONLINE                                     ║
║  > Legal: /privacy | /terms                           ║
╚════════════════════════════════════════════════════════╝
            `.trim()}
          </pre>
        </Container>
      </footer>
    )
  }

  // Mac OS Classic Layout
  if (themeConfig.id === 'mac-os-classic') {
    return (
      <footer
        style={{
          backgroundColor: '#ddd',
          borderTop: '2px solid #000',
          padding: 'var(--spacing-sm) 0',
          marginTop: 'auto',
        }}
      >
        <Container size="xl">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: '0.75rem',
              fontFamily: 'Chicago, Geneva, sans-serif',
            }}
          >
            <span>© {currentYear} Agustin Musi</span>
            <span>
              <a href="/privacy" style={{ color: '#000', textDecoration: 'underline' }}>
                Privacy
              </a>
              {' | '}
              <a href="/terms" style={{ color: '#000', textDecoration: 'underline' }}>
                Terms
              </a>
              {' | '}
              GitHub: @auge2u | @a2m2c
            </span>
          </div>
        </Container>
      </footer>
    )
  }

  // Executive Dashboard Layout
  if (themeConfig.id === 'executive-dashboard') {
    return (
      <footer
        style={{
          backgroundColor: 'transparent',
          borderTop: '1px solid rgba(0, 0, 0, 0.05)',
          padding: 'var(--spacing-lg) 0',
          marginTop: 'auto',
        }}
      >
        <Container size="xl">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: '0.75rem',
              color: 'var(--color-text-secondary)',
            }}
          >
            <span>
              © {currentYear} •{' '}
              <a href="/privacy" style={{ color: 'var(--color-text-secondary)', textDecoration: 'none' }}>
                Privacy
              </a>
              {' • '}
              <a href="/terms" style={{ color: 'var(--color-text-secondary)', textDecoration: 'none' }}>
                Terms
              </a>
            </span>
            <span>Agustin Musi • Full-Stack Engineer</span>
          </div>
        </Container>
      </footer>
    )
  }

  // Fallback
  return null
}
